import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking, performance monitoring, and logging
 *
 * Environment variables required:
 * - VITE_SENTRY_DSN: Sentry Data Source Name (DSN) for the project
 * - VITE_SENTRY_ENVIRONMENT: Environment name (e.g., 'development', 'staging', 'production')
 * - VITE_SENTRY_RELEASE: Release version (optional, defaults to package version)
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

  // Skip initialization if DSN is not provided (e.g., in local development)
  if (!dsn) {
    console.warn('Sentry DSN not provided. Sentry will not be initialized.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    enableLogs: true,
    
    // Trace propagation targets - which origins to trace for performance
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.scribebrain\.com/,
      /^https:\/\/authdev\.scribebrain\.com/,
    ],
    
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Replay integration for session recording
      Sentry.replayIntegration({
        maskAllText: false, // No sensitive data - show all text
        blockAllMedia: false, // No sensitive data - show all media
      }),

      // Console logging integration - automatically capture console errors
      Sentry.consoleLoggingIntegration({
        levels: ['error'], // Only capture console.error by default
      }),
    ],

    // Performance monitoring sample rate (0.0 to 1.0)
    // 1.0 = 100% of transactions are sent to Sentry
    tracesSampleRate: 1.0,

    // Session replay sample rate (0.0 to 1.0)
    replaysSessionSampleRate: 1.0,

    // Replay sample rate when an error occurs (0.0 to 1.0)
    replaysOnErrorSampleRate: 1.0,

    // Filter out sensitive data and unwanted errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Don't send AbortError (user-initiated cancellations)
      if (error && typeof error === 'object' && 'name' in error) {
        if (error.name === 'AbortError') {
          return null;
        }
      }

      // Only scrub Authorization header from API calls (contains auth token)
      // Everything else is visible - no sensitive data in the app
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
      }

      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      // User cancellations
      'AbortError',
    ],
  });
}

/**
 * Get the Sentry logger instance for structured logging
 * Usage:
 *   const { logger } = getSentryLogger();
 *   logger.info('User logged in', { userId: 123 });
 */
export function getSentryLogger() {
  return { logger: Sentry.logger };
}

/**
 * Utility to wrap async functions with error tracking
 * Usage:
 *   const result = await withErrorTracking(
 *     () => apiCall(),
 *     'API Call Failed',
 *     { endpoint: '/api/notes' }
 *   );
 */
export async function withErrorTracking<T>(
  fn: () => Promise<T>,
  errorMessage: string,
  context?: Record<string, unknown>
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        errorType: 'async_operation',
      },
      contexts: {
        operation: {
          message: errorMessage,
          ...context,
        },
      },
    });
    throw error;
  }
}
